import React from 'react';
import { FiChevronDown, FiClock } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { confirm } from '@/lib/confirm';
import usePermission from '@/hooks/usePermission';
import { useTranslation } from 'react-i18next';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import { BsFileEarmarkCheck } from 'react-icons/bs';
import { Card, CardBody, CardBodyContent, CardBodyHeader, CardBodyHeaderContent, CardContent, CardFooter, CardIcon, CardIconImg, CardKpi, CardKpiGrid, CardTitle } from '@/components/ui/card';
import { Badge, BadgeGroup } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export function ComplementaryActivityCard(props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activity, onDelete } = props;
  const authorityLevel = useAuthorityLevel();

  const activityStatus = activity['activity-status-or-submitted-count'];
  const activeAction = authorityLevel === 'student' && activityStatus !== t('activity.activity_not_submitted');

  const activityStatusClass = () => {
    switch (activity['activity-status-or-submitted-count']) {
      case t('activity.activity_not_submitted'):
        return 'danger';
      case t('activity.activity_submitted'):
        return 'success';
      case t('activity.activity_evaluated'):
        return 'success';
      case t('activity.awaiting_submission'):
        return 'warning';
      default:
        return 'warning';
    }
  };

  const getClocks = () => {
    switch (activity['class-hours']) {
      case 0.5:
        return (
          <CardIconImg
            isSemicircle
            className="min-w-4"
          >
            <FiClock />
          </CardIconImg>
        );
      case 1:
        return <FiClock />;
      case 1.5:
        return (
          <>
            <FiClock />
            <CardIconImg
              isSemicircle
              className="min-w-4"
            >
              <FiClock />
            </CardIconImg>
          </>
        );
      case 2:
        return (
          <>
            <FiClock />
            <FiClock />
          </>
        );
      case 2.5:
        return (
          <>
            <FiClock />
            <FiClock />
            <CardIconImg
              isSemicircle
              className="min-w-4"
            >
              <FiClock />
            </CardIconImg>
          </>
        );
      default:
        return (
          <>
            <FiClock />
            <FiClock />
            <FiClock />
          </>
        );
    }
  };

  const openCard = () => {
    if (authorityLevel === 'student') {
      if (activityStatus === t('activity.awaiting_submission')) {
        navigate(`/atividade-complementar/atividade/${activity.id}`);
      }
      if ([t('activity.activity_submitted'), t('activity.activity_evaluated')].includes(activityStatus)) {
        navigate(`/atividade-complementar/avaliacao/${activity?.id}/${activity['activity-student-id']}`);
      }
    }
  };

  const renderFooter = () => {
    if (activeAction && authorityLevel === 'student') {
      return (
        <CardFooter>
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={openCard}
          >
            {t('button.access')}
          </Button>
        </CardFooter>
      );
    }

    if (!activeAction && authorityLevel === 'student') {
      return (
        <CardFooter style={{ cursor: 'not-allowed' }}>
          <Button
            variant="outline"
            size="sm"
            fullWidth
            disabled
          >
            {t('button.access')}
          </Button>
        </CardFooter>
      );
    }

    if (usePermission('Visualizar e avaliar respostas', 'Arquivos e atividades complementares')) {
      return (
        <CardFooter>
          <ButtonGroup className="w-full">
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => navigate(`/atividade-complementar/avaliar-atividades/${activity.id}`)}
            >
              {t('activity.evaluate')}
            </Button>
            {(usePermission('Excluir atividades', 'Arquivos e atividades complementares') ||
              usePermission('Editar atividades', 'Arquivos e atividades complementares') ||
              usePermission('Visualizar e avaliar respostas', 'Arquivos e atividades complementares')) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                  >
                    <FiChevronDown size={20} />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                  {usePermission('Editar atividades', 'Arquivos e atividades complementares') && (
                    <DropdownMenuItem onClick={() => navigate(`/curso/atividades/criar/nome/${activity?.['learning-system-id']}/${activity?.['course-id']}/${activity?.id}`)}>{t('button.edit')}</DropdownMenuItem>
                  )}
                  {usePermission('Excluir atividades', 'Arquivos e atividades complementares') && (
                    <DropdownMenuItem
                      onClick={async () => {
                        const confirmed = await confirm({
                          title: t('projects.warningDeleteActivity'),
                          description: t('projects.warningDeleteCannotBeUndoneText'),
                          confirmText: t('button.yes'),
                          cancelText: t('button.no')
                        });
                        if (confirmed) onDelete();
                      }}
                    >
                      {t('button.delete')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem>
                    <Link to={`/atividade-complementar/dados/${activity.id}`}>{t('button.viewData')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Link to={activity['activity-file']?.url || activity['activity-file']?.file}>Acessar arquivo</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </ButtonGroup>
        </CardFooter>
      );
    }
  };

  return (
    <Card isDanger={activity['learning-system-activities'][0]?.is_required && authorityLevel === 'student'}>
      <CardContent>
        <CardIcon>
          <CardIconImg>
            <BsFileEarmarkCheck />
          </CardIconImg>
        </CardIcon>

        <CardBody>
          <CardBodyHeader>
            <CardBodyHeaderContent>
              <CardTitle title={activity.title}>{activity.title.length > 80 ? `${activity.title.slice(0, 80)}...` : activity.title}</CardTitle>
            </CardBodyHeaderContent>
            <BadgeGroup>
              {activity['activity-status-or-submitted-count'] && (
                <Badge
                  size="xs"
                  variant={activityStatusClass()}
                >
                  {activity['activity-status-or-submitted-count']}
                </Badge>
              )}
              {activity['learning-system-activities'][0]?.is_required && authorityLevel === 'student' && (
                <Badge
                  size="xs"
                  variant="danger"
                >
                  {t('warning.required')}
                </Badge>
              )}
            </BadgeGroup>
          </CardBodyHeader>
          <CardBodyContent>
            <CardKpiGrid>
              <CardKpi
                title={activity['class-hours'] < 2 ? `${activity['class-hours']} ${t('tasks.hour')}` : `${activity['class-hours']} ${t('tasks.hours')}`}
                aria-label={activity['class-hours'] < 2 ? `${activity['class-hours']} ${t('tasks.hour')}` : `${activity['class-hours']} ${t('tasks.hours')}`}
              >
                {getClocks()}
              </CardKpi>
            </CardKpiGrid>
          </CardBodyContent>
        </CardBody>
      </CardContent>

      {renderFooter()}
    </Card>
  );
}
